import AttendanceComponent from "@/components/user/attendance/attendance-page";
import { DashboardHeader } from "@/components/dashboard-header";
import { SidebarInset } from "@/components/ui/sidebar";
import AttendanceReportWeeklyCalendar from "@/components/user/attendance/attendance-report-weekly";
import PageHeader from "@/components/reusable-components/header/page-header";
import useFetchMyTimeSheet from "@/hooks/api/timesheet/useFetchMyTimeSheets";
import useMyAttendanceSummary from "@/hooks/api/attendance-summary/useMyAttendanceSummary";
import useMyShiftAssignments from "@/hooks/api/shift-assignment/useMyShiftAssignment";
import { createFileRoute } from "@tanstack/react-router";
import { nowInOrgTimezone } from "@/lib/date-utils";
import { type JSX } from "react";

export const Route = createFileRoute("/user/_layout/attendance/")({
  component: Attendance,
});

function Attendance(): JSX.Element {
  const myTimeSheetsQuery = useFetchMyTimeSheet({
    start_date: nowInOrgTimezone().subtract(6, "day").format("YYYY-MM-DD"),
    end_date: nowInOrgTimezone().format("YYYY-MM-DD"),
    page_size: 100,
  });
  const myTimeSheets = myTimeSheetsQuery.data?.results ?? null;
  const myTimeSheetsLoading = myTimeSheetsQuery.isPending;

  const myShiftAssignmentsQuery = useMyShiftAssignments({
    // Include yesterday: after midnight an overnight shift's active
    // assignment is dated the previous day.
    start_date: nowInOrgTimezone().subtract(1, "day").format("YYYY-MM-DD"),
    end_date: nowInOrgTimezone().format("YYYY-MM-DD"),
    page_size: 100,
  });
  const myShiftAssignments = myShiftAssignmentsQuery.data?.results ?? [];
  const myShiftAssignmentsLoading = myShiftAssignmentsQuery.isPending;

  const myAttendanceSummaryQuery = useMyAttendanceSummary({
    start_date: nowInOrgTimezone().subtract(6, "day").format("YYYY-MM-DD"),
    end_date: nowInOrgTimezone().format("YYYY-MM-DD"),
  });
  const myAttendanceSummary = myAttendanceSummaryQuery.data ?? null;
  const myAttendanceSummaryLoading = myAttendanceSummaryQuery.isPending;

  // A day with no punch has no TimeSheet row at all, so this can legitimately
  // come back empty — that's a valid "no check-in yesterday" answer, not an
  // error state.
  const yesterdaysTimeSheetQuery = useFetchMyTimeSheet({
    date: nowInOrgTimezone().subtract(1, "day").format("YYYY-MM-DD"),
    page_size: 1,
  });
  const yesterdaysTimeSheet = yesterdaysTimeSheetQuery.data?.results?.[0] ?? null;
  const yesterdaysTimeSheetLoading = yesterdaysTimeSheetQuery.isPending;

  // Re-run these queries after the check-in/check-out flow mutates a
  // timesheet (attendance-page.tsx's check-in/out hooks are a different
  // domain and still call this imperatively).
  const fetchLast7Days = () => {
    myTimeSheetsQuery.refetch();
    myAttendanceSummaryQuery.refetch();
    yesterdaysTimeSheetQuery.refetch();
  };

  return (
    <SidebarInset>
      <DashboardHeader />
      <div className="p-6 pt-0 pb-4">
        <div className="mx-auto space-y-4">
          <PageHeader
            title="Attendance"
            description="Keep track of your hours, complete tasks, and stay updated with your weekly progress."
          />

          <AttendanceReportWeeklyCalendar
            myAttendanceSummary={myAttendanceSummary}
            myAttendanceSummaryLoading={myAttendanceSummaryLoading}
            myTimeSheets={myTimeSheets}
            myTimeSheetsLoading={myTimeSheetsLoading}
            yesterdaysTimeSheet={yesterdaysTimeSheet}
            yesterdaysTimeSheetLoading={yesterdaysTimeSheetLoading}
            onYesterdaysTimeSheetChange={fetchLast7Days}
          />
          <AttendanceComponent
            refreshData={fetchLast7Days}
            myShiftAssignments={myShiftAssignments}
            myShiftAssignmentsLoading={myShiftAssignmentsLoading}
          />
        </div>
      </div>
    </SidebarInset>
  );
}
